import { useAnalyticContext } from '@application/analytic/analytic_context.provider';
import { ScreenInformationGathering } from '@feature/information-gathering/screens';
import { Skeleton } from '@view/view_create_new_message/components/skeleton';
import { ViewNotFound } from '@view/view_not_found/view_not_found_route.component';
import { useEffect, useRef } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router';
import { useBasket } from '../hooks/use-basket';
import { useCreateNewMessage } from '../hooks/use-create-new-message';
import { useMessagingInformationGathering } from '../hooks/use-messaging-information-gathering';
import { ScreenAccess } from '../screens/access';
import { ScreenClosed } from '../screens/closed';
import { ScreenConclusion } from '../screens/conclusion';
import { ScreenDone } from '../screens/done';
import { ScreenError } from '../screens/error';
import { ScreenForms } from '../screens/forms';
import { ScreenNhsLoginRequired } from '../screens/nhs-login-required';
import { ScreenRfv } from '../screens/reason_for_visit';
import { ScreenStart } from '../screens/screen_start';
import { ScreenSetup } from '../screens/setup';
import { ScreenWhoIsRespondent } from '../screens/who_is_respondent';

export const CreateNewMessageRoutes = () => {
	const { isClosed, loadSettings, setupNewBasket, flowSettings, flowData } = useCreateNewMessage();
	const { basketId } = useBasket();
	const analyticCtx = useAnalyticContext();
	const hasLoadedSettings = useRef(false);
	const nestedRoute = useParams<'*'>()['*'];

	useEffect(() => {
		// The index route renders ScreenSetup, which owns deleting the previous basket
		// and creating a new one. Running deep-link recovery at the same time can restore
		// the old basket just before ScreenSetup deletes it, leaving the flow with a stale id.
		if ((nestedRoute ?? '') === '' || flowData.flowState !== 'accessible' || isClosed || hasLoadedSettings.current) {
			return;
		}

		hasLoadedSettings.current = true;

		analyticCtx.trackEvent('Load settings');

		// On a full-page entry to a deep screen (e.g. returning from the IdP-based
		// representation switch, which reloads the app on this screen), the previous
		// identity's active basket is no longer reachable. Create a fresh basket so
		// the screen can render and continue; let all other failures surface unchanged.
		loadSettings().catch((error: unknown) => {
			if (error instanceof Error && error.message === 'WE NEED A BASKET!!') {
				void setupNewBasket();
				return;
			}

			throw error;
		});
	}, [nestedRoute, flowData.flowState, isClosed, loadSettings, setupNewBasket, analyticCtx]);

	if (flowData.flowState === 'pending') {
		return (
			<Routes>
				<Route path='*' element={<Skeleton />} />
			</Routes>
		);
	}

	if (isClosed) {
		return (
			<Routes>
				<Route index element={<ScreenClosed />} />
				<Route path='closed'>
					<Route index element={<ScreenClosed />} />
				</Route>
				<Route path='error'>
					<Route index element={<ScreenError />} />
				</Route>
				<Route path='*' element={<ViewNotFound />} />
			</Routes>
		);
	}

	if (flowSettings.flowState === 'pending' || basketId == null) {
		return (
			<Routes>
				<Route index element={<ScreenSetup />} />
				<Route path='*' element={<Skeleton />} />
			</Routes>
		);
	}

	return (
		<Routes>
			<Route index element={<ScreenSetup />} />
			<Route path='closed' element={<Navigate to='..' replace />} />
			<Route path='start'>
				<Route index element={<ScreenStart />} />
			</Route>
			<Route path='who-is-respondent/*' element={<ScreenWhoIsRespondent />} />
			<Route path='information-gathering' element={<MessagingInformationGathering />} />
			<Route path='nhs-login-required' element={<ScreenNhsLoginRequired />} />
			<Route path='reason-for-visit'>
				<Route index element={<ScreenRfv />} />
			</Route>
			<Route path='forms/*' element={<ScreenForms />} />
			<Route path='access/*' element={<ScreenAccess />} />
			<Route path='conclusion'>
				<Route index element={<ScreenConclusion />} />
			</Route>
			<Route path='done' element={<ScreenDone />} />
			<Route path='done/:conversationId' element={<ScreenDone />} />
			<Route path='error'>
				<Route index element={<ScreenError />} />
			</Route>
			<Route path='*' element={<ViewNotFound />} />
		</Routes>
	);
};

function MessagingInformationGathering() {
	const informationGathering = useMessagingInformationGathering();
	return <ScreenInformationGathering error={<ScreenError />} onComplete={informationGathering.complete} />;
}
